import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { BLOCKS } from 'src/consts'

const makeReducerFactoryWithPredefinedParameters =
  ({ k, x, y, boxX, boxY, boxWidth, boxHeight }) =>
  (minWidth, minHeight, multiplyCoef, childReducer) =>
  (acc, d, i) => {
    const { x0, y0, x1, y1, children } = d
    const leaveWidth = x1 - x0
    const leaveHeight = y1 - y0

    if (
      (boxX <= x0 &&
        x0 <= boxX + boxWidth &&
        boxY <= y0 &&
        y0 <= boxY + boxHeight) ||
      (boxX <= x1 &&
        x1 <= boxX + boxWidth &&
        boxY <= y0 &&
        y0 <= boxY + boxHeight) ||
      (boxX <= x0 &&
        x0 <= boxX + boxWidth &&
        boxY <= y1 &&
        y1 <= boxY + boxHeight) ||
      (boxX <= x1 &&
        x1 <= boxX + boxWidth &&
        boxY <= y1 &&
        y1 <= boxY + boxHeight) ||
      (boxX > x0 && x1 > boxX + boxWidth) ||
      (boxY > y0 && y1 > boxY + boxHeight)
    ) {
      if (leaveWidth * k >= minWidth && leaveHeight * k >= minHeight) {
        let displayName

        if (!multiplyCoef) {
          displayName = leaveWidth >= minWidth && leaveHeight >= minHeight
        }

        if (!children) {
          return {
            ...acc,
            [i]: {
              displayName,
              x0: d.x0 * k + x,
              x1: d.x1 * k + x,
              y0: d.y0 * k + y,
              y1: d.y1 * k + y,
            },
          }
        } else if (childReducer) {
          return {
            ...acc,
            [i]: {
              displayName,
              x0: d.x0 * k + x,
              x1: d.x1 * k + x,
              y0: d.y0 * k + y,
              y1: d.y1 * k + y,
              children: children.reduce(childReducer, {}),
            },
          }
        }
      }

      return acc
    }

    return acc
  }

export const useSlicedData = ({ data, transform, width, height }) => {
  const releaseTimeout = useRef()
  const prevent = useRef(false)
  const updated = useRef({ data, transform, height, width })

  const [map, setMap] = useState(data ? data.groups : [])

  const [state, setState] = useState({
    transform,
    locked: false,
  })

  const [diff, setDiff] = useState({ k: 1, x: 0, y: 0 })

  const reducer = useMemo(
    () =>
      makeReducerFactoryWithPredefinedParameters({
        x: transform.x,
        y: transform.y,
        k: transform.k,
        boxX: Math.abs(transform.x) / transform.k,
        boxY: Math.abs(transform.y) / transform.k,
        boxWidth: width / transform.k,
        boxHeight: height / transform.k,
      }),
    [transform, height, width]
  )

  useEffect(() => {
    if (!state.locked) {
      if (!prevent.current) {
        window.requestAnimationFrame(() => {
          const map = data
            ? data.groups.reduce(
                reducer(
                  BLOCKS.MIN_GROUP_WIDTH,
                  BLOCKS.MIN_GROUP_HEIGHT,
                  false,
                  reducer(
                    BLOCKS.MIN_LEAF_TEXT_WIDTH,
                    BLOCKS.MIN_LEAF_TEXT_HEIGHT,
                    true
                  )
                ),
                {}
              )
            : []

          setMap(map)
        })
      } else {
        prevent.current = false
      }
    }
  }, [data, reducer, state])

  useEffect(() => {
    window.requestAnimationFrame(() => {
      const k = transform.k / state.transform.k

      setDiff({
        x: transform.x - state.transform.x * k,
        y: transform.y - state.transform.y * k,
        k: transform.k / state.transform.k,
      })
    })
  }, [transform, state])

  useEffect(() => {
    updated.current = { data, transform, height, width }
  }, [data, transform, height, width])

  const lock = useCallback(() => {
    if (releaseTimeout.current) {
      clearTimeout(releaseTimeout.current)
    }

    if (!state.locked) {
      setState({ locked: true, transform })
    }
  }, [transform, state])

  const release = useCallback(() => {
    if (state.locked) {
      releaseTimeout.current = setTimeout(() => {
        prevent.current = true

        window.requestAnimationFrame(() => {
          const { data, transform, height, width } = updated.current

          const reducer = makeReducerFactoryWithPredefinedParameters({
            x: transform.x,
            y: transform.y,
            k: transform.k,
            boxX: Math.abs(transform.x) / transform.k,
            boxY: Math.abs(transform.y) / transform.k,
            boxWidth: width / transform.k,
            boxHeight: height / transform.k,
          })

          const map = data
            ? data.groups.reduce(
                reducer(
                  BLOCKS.MIN_GROUP_WIDTH,
                  BLOCKS.MIN_GROUP_HEIGHT,
                  false,
                  reducer(
                    BLOCKS.MIN_LEAF_TEXT_WIDTH,
                    BLOCKS.MIN_LEAF_TEXT_HEIGHT,
                    true
                  )
                ),
                {}
              )
            : []

          setMap(map)
          setState({ locked: false, transform })
        })
      }, 50)
    }
  }, [state])

  return {
    ...state,
    map,
    diff,
    lock,
    release,
  }
}
